import React, { useState } from 'react';
import { FieldData } from "./DiffFieldComponents";
import IconProvider from "@/components/ui/IconProvider";
import { AVAILABLE_FIELDS } from "@/lib/utils/fieldDefinitions";
import { getWizardElement } from "@/components/wizards/WizardElements";
import { isTextLikeField, getTypeElementInfo, getGlossaryItemInfo } from "@/components/wizards/glossary/SectionFormFunctions/objectFormUtils";
import { useTypesDataContext } from "@/providers/TypesDataProvider";
import { useGlossaryDataContext } from "@/providers/ObjectsDataProvider";

interface FieldContentProps {
  field: FieldData;
  showId?: boolean;
  className?: string;
  showPrefix?: boolean;
  prefix?: string;
  mode?: 'view' | 'edit';
  onFieldChange?: (fieldId: string, value: any) => void;
}

export const FieldContent: React.FC<FieldContentProps> = ({ 
  field, 
  className = "", 
  showPrefix = false, 
  prefix = "",
  mode = 'view',
  onFieldChange
}) => {
  // Get field type definition for label
  const fieldTypeDef = AVAILABLE_FIELDS[field.type as keyof typeof AVAILABLE_FIELDS];
  const fieldTypeLabel = fieldTypeDef?.label || field.type;

  // Get context data for types and glossary items
  const { types = [] } = useTypesDataContext();
  const { glossaryItems = [] } = useGlossaryDataContext();
  const [isExpanded, setIsExpanded] = useState(false);
  
  const isEditMode = mode === 'edit';

  // Render the field value like the wizard does in view mode
  const renderFieldValue = () => {
    const isEmpty = field.value === undefined || field.value === null || field.value === "";
    
    // In view mode, show "Not set" for empty fields
    // In edit mode, always render the field so it can be filled in
    if (isEmpty && !isEditMode) {
      return <span className="text-gray-400 italic text-sm">Not set</span>;
    }

    // Special handling for types field
    if (field.type === "types") {
      let selectedTypes: string[] = [];
      if (Array.isArray(field.value)) {
        selectedTypes = field.value;
      } else if (typeof field.value === "string" && field.value.trim()) {
        selectedTypes = field.value
          .split(",")
          .map((id) => id.trim())
          .filter((id) => id);
      }

      // If no types selected
      if (selectedTypes.length === 0) {
        // In edit mode, render the wizard element so user can select types
        if (isEditMode) {
          try {
            const wizardElement = getWizardElement(
              field as any,
              field.value || "",
              (value: any) => onFieldChange && onFieldChange(field.id, value),
              (value: any) => onFieldChange && onFieldChange(field.id, value),
              undefined
            );
            return React.cloneElement(wizardElement, { disabled: false });
          } catch (error) {
            return <span className="text-gray-400 italic text-sm">No types selected</span>;
          }
        }
        // In view mode, show placeholder
        return <span className="text-gray-400 italic text-sm">No types selected</span>;
      }

      const displayTypes = isExpanded || selectedTypes.length === 1 
        ? selectedTypes 
        : selectedTypes.slice(0, 1);
      const hasMore = selectedTypes.length > 1;

      return (
        <div className="space-y-1.5">
          {displayTypes.map((typeId: string, index: number) => {
            const typeInfo = getTypeElementInfo(typeId, types);
            return (
              <div key={index} className="p-2 bg-gray-50 rounded">
                <div className="font-medium text-sm text-gray-900">
                  {typeInfo.name}
                </div>
                {typeInfo.description && (
                  <div className="text-xs text-gray-600 mt-0.5">
                    {typeInfo.description}
                  </div>
                )}
              </div>
            );
          })}
          {hasMore && (
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="text-sm text-purple-600 hover:text-purple-800 font-medium cursor-pointer hover:underline"
            >
              {isExpanded ? "Show less" : `+${selectedTypes.length - 1} more`}
            </button>
          )}
        </div>
      );
    }

    // Special handling for glossaryItem field
    if (field.type === "glossaryItem") {
      let selectedItems: string[] = [];
      if (Array.isArray(field.value)) {
        selectedItems = field.value;
      } else if (typeof field.value === "string" && field.value.trim()) {
        selectedItems = field.value
          .split(",")
          .map((id) => id.trim())
          .filter((id) => id);
      }

      const validItems = selectedItems.filter((itemId) => {
        const itemInfo = getGlossaryItemInfo(itemId, glossaryItems);
        return !itemInfo.name.startsWith("Unknown Item");
      });

      // If no items selected
      if (validItems.length === 0) {
        // In edit mode, render the wizard element so user can select items
        if (isEditMode) {
          try {
            const wizardElement = getWizardElement(
              field as any,
              field.value || "",
              (value: any) => onFieldChange && onFieldChange(field.id, value),
              (value: any) => onFieldChange && onFieldChange(field.id, value),
              undefined
            );
            return React.cloneElement(wizardElement, { disabled: false });
          } catch (error) {
            return <span className="text-gray-400 italic text-sm">No items selected</span>;
          }
        }
        // In view mode, show placeholder
        return <span className="text-gray-400 italic text-sm">No items selected</span>;
      }

      const displayItems = isExpanded || validItems.length === 1 
        ? validItems 
        : validItems.slice(0, 1);
      const hasMore = validItems.length > 1;

      return (
        <div className="space-y-1.5">
          {displayItems.map((itemId: string, index: number) => {
            const itemInfo = getGlossaryItemInfo(itemId, glossaryItems);
            return (
              <div key={index} className="p-2 bg-gray-50 rounded">
                <div className="font-medium text-sm text-gray-900">
                  {itemInfo.name}
                </div>
              </div>
            );
          })}
          {hasMore && (
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="text-sm text-purple-600 hover:text-purple-800 font-medium cursor-pointer hover:underline"
            >
              {isExpanded ? "Show less" : `+${validItems.length - 1} more`}
            </button>
          )}
        </div>
      );
    }

    // For translatable or text-like fields, use the wizard element
    if (field.translation || isTextLikeField(field.type)) {
      try {
        // Use empty string for undefined/null values in edit mode so fields can be filled in
        const fieldValue = isEmpty && isEditMode ? "" : field.value;
        
        const wizardElement = getWizardElement(
          { ...field, type: field.translation || isTextLikeField(field.type) ? "textarea" : field.type } as any,
          fieldValue,
          isEditMode && onFieldChange ? (value: any) => onFieldChange(field.id, value) : () => {},
          isEditMode && onFieldChange ? (value: any) => onFieldChange(field.id, value) : () => {},
          undefined
        );

        // Clone the element and add appropriate props based on mode
        const element = React.cloneElement(wizardElement, {
          disabled: !isEditMode,
          viewMode: isEditMode ? undefined : ("view-formatted" as const),
        });

        return element;
      } catch (error) {
        // Fallback to simple display
        if (isEditMode) {
          // In edit mode, show an editable input even on error
          return <input 
            type="text" 
            value={field.value || ""} 
            onChange={(e) => onFieldChange && onFieldChange(field.id, e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded"
          />;
        }
        return <div className="text-gray-900 text-sm whitespace-pre-wrap">{String(field.value || "")}</div>;
      }
    }

    // For other field types, use the wizard element
    try {
      // Use appropriate empty value for the field type in edit mode
      const fieldValue = isEmpty && isEditMode ? "" : field.value;
      
      const wizardElement = getWizardElement(
        field as any,
        fieldValue,
        isEditMode && onFieldChange ? (value: any) => onFieldChange(field.id, value) : () => {},
        isEditMode && onFieldChange ? (value: any) => onFieldChange(field.id, value) : () => {},
        undefined
      );

      // Clone the element and add appropriate props based on mode
      const element = React.cloneElement(wizardElement, {
        disabled: !isEditMode,
      });

      return element;
    } catch (error) {
      // Fallback to simple value display if wizard element fails
      if (isEditMode) {
        // In edit mode, show an editable input even on error
        return <input 
          type="text" 
          value={field.value || ""} 
          onChange={(e) => onFieldChange && onFieldChange(field.id, e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded"
        />;
      }
      return <div className="text-gray-900 text-sm">{String(field.value || "")}</div>;
    }
  };

  return (
    <div className={`space-y-2 ${className}`}>
      {/* Field Header */}
      <div className="p-2 border border-gray-200 rounded-sm bg-white">
        <div className="flex items-center gap-2 flex-wrap">
          {showPrefix && prefix && (
            <span className="font-mono text-sm font-medium">{prefix}</span>
          )}
          {field.translation && (
            <IconProvider iconstring="fa-globe" size={14} className="text-muted-foreground" />
          )}
          <div className="font-medium text-gray-900">
            {field.label}
            {field.mandatory && (
              <span className="text-purple-1 ml-1">*</span>
            )}
          </div>
          <span className="text-sm text-gray-600">• {fieldTypeLabel}</span>
          {field.multiple && (
            <span className="text-xs px-2 py-0.5 rounded bg-blue-100 text-blue-700">
              Multiple
            </span>
          )}
          {field.filter && (
            <span className="text-xs px-2 py-0.5 rounded bg-purple-100 text-purple-700">
              Filtered
            </span>
          )}
        </div>
      </div>

      {/* Field Value Display using wizard elements */}
      <div className="ml-4">
        {renderFieldValue()}
      </div>

      {/* Field Documentation Display - if present */}
      {field.documentation && (
        <div className="ml-4 p-2 rounded text-sm bg-blue-50 border-l-4 border-blue-400">
          <div className="flex items-start gap-2">
            <IconProvider 
              iconstring="fa-circle-info" 
              size={12} 
              className="mt-0.5 text-blue-600" 
            />
            <div 
              className="text-gray-700 flex-1 text-sm"
              dangerouslySetInnerHTML={{ __html: field.documentation }}
            />
          </div>
        </div>
      )}
    </div>
  );
};
